import { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Loader2, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const [status, setStatus] = useState('polling'); // polling | paid | failed | timeout
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  const attemptsRef = useRef(0);
  const timerRef = useRef(null);
  const doneRef = useRef(false);

  useEffect(() => {
    document.title = 'Pagamento — AeroDeals';
    if (!sessionId) { setStatus('failed'); setErr('Parametro session mancante'); return; }

    const POLL_INTERVAL = 2000;
    const MAX_ATTEMPTS = 10;

    const poll = async () => {
      if (doneRef.current) return;
      attemptsRef.current += 1;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setData(data);
        if (data.payment_status === 'paid') {
          doneRef.current = true;
          setStatus('paid');
          return;
        }
        if (data.status === 'expired') {
          doneRef.current = true;
          setStatus('failed');
          setErr('Sessione di pagamento scaduta.');
          return;
        }
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          doneRef.current = true;
          setStatus('timeout');
          return;
        }
        timerRef.current = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          doneRef.current = true;
          setStatus('failed');
          setErr(e.response?.data?.detail || 'Errore verifica pagamento');
        } else {
          timerRef.current = setTimeout(poll, POLL_INTERVAL);
        }
      }
    };
    poll();
    return () => { doneRef.current = true; if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId]);

  const goToNext = () => {
    const ptype = data?.metadata?.type;
    const listingId = data?.metadata?.listing_id;
    if (ptype === 'listing_plan' || ptype === 'featured') {
      navigate('/dashboard');
    } else if (ptype === 'loi') {
      navigate('/lettera-di-intenti-compravendita-velivolo-tra-privati');
    } else {
      navigate('/');
    }
  };

  return (
    <div className="min-h-screen bg-[#09090B] pt-24 flex items-center justify-center px-4" data-testid="payment-success-page">
      <div className="max-w-md w-full text-center">
        {status === 'polling' && (
          <>
            <Loader2 size={42} className="text-[#C5A059] mx-auto mb-5 animate-spin" />
            <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Verifichiamo il pagamento
            </h1>
            <p className="text-sm text-zinc-500 leading-relaxed">Stripe sta confermando la transazione. Di solito ci vogliono pochi secondi.</p>
            <p className="text-[10px] font-mono uppercase tracking-widest text-zinc-600 mt-6">attempt {attemptsRef.current}/10</p>
          </>
        )}
        {status === 'paid' && (
          <>
            <CheckCircle size={52} className="text-emerald-400 mx-auto mb-5" />
            <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Pagamento confermato
            </h1>
            <p className="text-sm text-zinc-400 leading-relaxed mb-2">
              Grazie! La tua transazione è andata a buon fine.
            </p>
            {data?.amount_total != null && (
              <p className="text-xs text-zinc-500 mb-6">
                Totale: <span className="text-zinc-200 font-mono">{(data.amount_total / 100).toFixed(2)} {data.currency?.toUpperCase()}</span>
              </p>
            )}
            <p className="text-xs text-zinc-500 mb-8">Riceverai una email di conferma a breve.</p>
            <button onClick={goToNext} className="bg-[#C5A059] text-black px-6 py-3 text-xs font-medium hover:bg-[#D4B271] transition-colors" data-testid="payment-success-next">
              Continua
            </button>
          </>
        )}
        {status === 'timeout' && (
          <>
            <AlertTriangle size={40} className="text-amber-400 mx-auto mb-4" />
            <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Conferma in elaborazione
            </h1>
            <p className="text-sm text-zinc-500 mb-6 leading-relaxed">La verifica sta richiedendo più tempo del previsto. Non preoccuparti: se il pagamento è riuscito riceverai una email di conferma entro pochi minuti.</p>
            <button onClick={() => navigate('/dashboard')} className="bg-[#C5A059] text-black px-5 py-2.5 text-xs font-medium hover:bg-[#D4B271]" data-testid="payment-success-dash">
              Vai alla Dashboard
            </button>
          </>
        )}
        {status === 'failed' && (
          <>
            <AlertTriangle size={40} className="text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl md:text-3xl tracking-tight font-medium text-white mb-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Pagamento non riuscito
            </h1>
            <p className="text-sm text-zinc-500 mb-6 leading-relaxed">{err || 'Si è verificato un errore.'}</p>
            <button onClick={() => navigate('/dashboard')} className="border border-zinc-700 text-zinc-300 px-5 py-2.5 text-xs hover:border-zinc-500">
              Torna alla Dashboard
            </button>
          </>
        )}
      </div>
    </div>
  );
}
